import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useUIStore } from '@/stores/use-ui-store';
import { PENDING_REFERRAL_CODE_KEY } from '@/lib/billing/pending-referral';

/**
 * Consumes a pending referral code left by /r/:code when a logged-out visitor
 * signs in. Mirrors InvitationBridge: fires once when isAuthenticated flips
 * false → true. Unlike invitations, there is no server-side accept step — the
 * code just needs a place to land, so this opens the personal subscription
 * tab where PromoCodeInput reads and clears PENDING_REFERRAL_CODE_KEY itself
 * on mount.
 *
 * Without this bridge the code sits in sessionStorage until the user
 * independently finds Settings → Subscription — for the referral link's
 * primary audience (a brand-new signup dropped into onboarding), that mostly
 * never happens.
 */
export function ReferralBridge() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || handled.current) return;
    const pending = sessionStorage.getItem(PENDING_REFERRAL_CODE_KEY);
    if (!pending) return;
    handled.current = true;
    setSettingsOpen(true, 'subscription');
    void navigate({ to: '/', replace: true });
  }, [isAuthenticated, navigate, setSettingsOpen]);

  return null;
}
